import { inject, Injectable } from "@angular/core";
import {
  HTMLPrintResult,
  PrintService
} from "src/app/services/print/print.service";
import { ReportDesignItemConfig } from "src/app/modules/bootstrap/modules/report/report-design-item";
import {
  renderReportTemplateToPdfObject,
  ReportPdfPrintObject
} from "src/app/modules/bootstrap/modules/report/report-renderer/report-template-renderer";
import { compact } from "lodash-es";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { UserBackend } from "src/app/backend/api/user.backend";

type ReportServiceHTMLOptions = {
  renderer: "components" | "data";
  componentsRenderers?: any[];
  headerTitle: string | undefined;
  preview?: boolean;
  onlyTextWithValue?: boolean;
  useFakeTextData?: boolean;
  dataUrlAttachments?: string[];
};

@Injectable()
export class ReportService {
  private readonly userBackend = inject(UserBackend);
  private readonly memberBackend = inject(MemberBackend);
  private readonly storageBackend = inject(StorageBackend);
  private readonly printService = inject(PrintService);

  async templateToHTML(
    template: ReportDesignItemConfig[],
    dataSource: Record<string, any>,
    options: ReportServiceHTMLOptions
  ): Promise<HTMLPrintResult> {
    const jsonData: string = await this.buildJsonData(
      template,
      dataSource,
      options,
      compact([options.preview ? { type: "preview-watermark" } : null])
    );

    const jsonDataWithoutEmptyFields =
      removeEmptyFieldsAndReplaceNewLines(jsonData);

    return await this.printService.toHTML(jsonDataWithoutEmptyFields, {
      dataUrlAttachments: options.dataUrlAttachments
    });
  }

  private async buildJsonData(
    template: ReportDesignItemConfig[],
    dataSource: Record<string, any>,
    options: ReportServiceHTMLOptions,
    extraComponents: Array<{ type: string }> = []
  ): Promise<string> {
    let results: ReportPdfPrintObject | undefined;

    switch (options.renderer) {
      case "components": {
        results = compact(
          await Promise.all(
            options
              .componentsRenderers!.filter((x) => x.level === 1)
              .map((x) =>
                !!x.renderPdfObject
                  ? x.renderPdfObject()
                  : Promise.resolve(null)
              )
          )
        );
        break;
      }
      case "data": {
        results = await renderReportTemplateToPdfObject(
          template,
          dataSource,
          {
            storageBackend: this.storageBackend,
            userBackend: this.userBackend,
            memberBackend: this.memberBackend
          },
          {
            onlyTextWithValue: !!options.onlyTextWithValue,
            useFakeTextData: false
          }
        );
        break;
      }
    }

    return JSON.stringify([...results, ...extraComponents]);
  }
}

function removeEmptyFieldsAndReplaceNewLines(jsonData: string): string {
  const data = JSON.parse(jsonData);

  const processedData = removeEmptyRootFieldsAndReplaceNewLines(data);

  return JSON.stringify(processedData);
}

type Item = {
  type:
    | "custom"
    | "tab"
    | "text"
    | "textarea"
    | "group-horizontal"
    | "group"
    | "group-vertical"
    | "image"
    | "check"
    | "selectbox";
  tabs?: ItemTab[];
  text?: string;
  html?: string;
  value?: string;
};

type ItemTab = {
  items: Item[];
};

function removeEmptyRootFieldsAndReplaceNewLines(
  items: Array<Item>
): Array<Item> {
  return compact(
    items.map((item) => {
      switch (item.type) {
        case "group":
        case "group-vertical":
        case "group-horizontal": {
          const itemsWithoutEmptyFields =
            removeEmptyRootFieldsAndReplaceNewLines((<any>item).items);

          if (itemsWithoutEmptyFields.length > 0) {
            (<any>item).items = itemsWithoutEmptyFields;
            return item;
          }

          return null;
        }
        case "custom": {
          if (!item.html) {
            return null;
          }

          return item;
        }
        case "tab": {
          const tabs = compact(
            item.tabs?.map((tab: ItemTab) => {
              const tabItems = compact(
                removeEmptyRootFieldsAndReplaceNewLines(tab.items)
              );

              if (tabItems.length === 0) {
                return null;
              }

              tab.items = tabItems;

              return tab;
            })
          );

          item.tabs = tabs;

          return tabs.length === 0 ? null : item;
        }
        case "image": {
          if (!(<any>item).data) {
            return null;
          }

          return item;
        }
        case "text": {
          if (!item.text) {
            return null;
          }

          item.text = replaceNewLineCharWithHtmlBrTag(item.text);

          return item;
        }
        case "textarea": {
          if (!item.value) {
            return null;
          }

          item.value = replaceNewLineCharWithHtmlBrTag(item.value);

          return item;
        }
        case "check": {
          if (!item.value) {
            return null;
          }

          return item;
        }
        case "selectbox": {
          if (!item.text) {
            return null;
          }

          return item;
        }
        default: {
          return item;
        }
      }
    })
  );
}

function replaceNewLineCharWithHtmlBrTag(text: string): string {
  return text.replace(/\n/gi, "<br/>");
}
